import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import {
  ChatCircleText,
  PaperPlaneTilt,
  Buildings,
  User,
  FloppyDisk,
  PencilSimple,
  Check,
  X,
  Trash,
} from '@phosphor-icons/react';
import useFormDraft from '@/hooks/useFormDraft';

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-ZA', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return iso; }
};

/**
 * A single note bubble with inline edit support.
 * The author (or any admin) can toggle Edit mode to update the text.
 */
const NoteBubble = ({ note, internal, caseId, currentUser, onUpdated, onDeleted }) => {
  const isAdminNote = note.author_role === 'admin';
  const isMyNote = currentUser?.user_id && note.author_user_id === currentUser.user_id;
  const isAdminUser = currentUser?.role === 'admin' || currentUser?.role === 'staff';
  const canEdit = isMyNote || isAdminUser;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.text || '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (trimmed === note.text) { setEditing(false); return; }
    setSaving(true);
    try {
      const url = internal
        ? `/cases/${caseId}/internal-notes/${note.note_id}`
        : `/cases/${caseId}/notes/${note.note_id}`;
      const { data } = await api.patch(url, { text: trimmed });
      toast.success('Note updated');
      setEditing(false);
      onUpdated?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => { setDraft(note.text || ''); setEditing(false); };

  const handleDelete = async () => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    setDeleting(true);
    try {
      const url = internal
        ? `/cases/${caseId}/internal-notes/${note.note_id}`
        : `/cases/${caseId}/notes/${note.note_id}`;
      await api.delete(url);
      toast.success('Note deleted');
      onDeleted?.(note.note_id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const wrapperClass = internal
    ? 'p-3 border border-amber-200 bg-amber-50/50'
    : `p-3 border ${isAdminNote ? 'border-[#4CAF50]/30 bg-[#4CAF50]/5' : 'border-[#0055FF]/20 bg-[#0055FF]/5'}`;

  const AuthorIcon = internal
    ? User
    : isAdminNote ? Buildings : User;
  const iconClass = internal
    ? 'text-amber-700'
    : isAdminNote ? 'text-[#4CAF50]' : 'text-[#0055FF]';

  return (
    <div className={wrapperClass} data-testid={`${internal ? 'internal-note' : 'note'}-${note.note_id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <AuthorIcon className={`w-4 h-4 shrink-0 ${iconClass}`} weight="fill" />
          <span className="text-sm font-semibold text-[#0A192F] truncate">
            {internal ? (note.author_name || 'Staff') : (isAdminNote ? 'Marema' : note.author_name)}
          </span>
          {!internal && isAdminNote && (
            <span className="text-xs text-gray-500 truncate">({note.author_name})</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-gray-500">{formatTime(note.created_at)}</span>
          {note.edited_at && !editing && (
            <span
              className="text-xs text-gray-400 italic"
              title={`Edited ${formatTime(note.edited_at)}${note.edit_count ? ` (${note.edit_count}×)` : ''}`}
              data-testid={`note-edited-${note.note_id}`}
            >
              · edited
            </span>
          )}
          {canEdit && !editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-gray-400 hover:text-[#0055FF] transition-colors"
              aria-label="Edit note"
              data-testid={`edit-note-btn-${note.note_id}`}
            >
              <PencilSimple className="w-3.5 h-3.5" />
            </button>
          )}
          {canEdit && !editing && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
              aria-label="Delete note"
              data-testid={`delete-note-btn-${note.note_id}`}
            >
              <Trash className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={5000}
            className="rounded-none text-sm"
            data-testid={`edit-note-input-${note.note_id}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleSave(); }
              if (e.key === 'Escape') { e.preventDefault(); handleCancel(); }
            }}
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={saving}
              className="rounded-none h-8"
              data-testid={`cancel-edit-${note.note_id}`}
            >
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={saving || !draft.trim() || draft.trim() === note.text}
              className={`rounded-none h-8 ${internal ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#4CAF50] hover:bg-[#45a049]'} text-white`}
              data-testid={`save-edit-${note.note_id}`}
            >
              <Check className="w-3.5 h-3.5 mr-1" />
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-800 whitespace-pre-wrap">{note.text}</p>
      )}
    </div>
  );
};

/**
 * NotesThread — shared component for displaying + posting the per-case notes thread.
 *
 * Props:
 *   caseId       — case document id
 *   notes        — array of { note_id, author_user_id, author_name, author_role, text, created_at, edited_at?, edit_count? }
 *   legacyNote   — optional string (legacy public_notes field, shown once at the top)
 *   onPosted     — callback(newNote)      → parent refreshes case
 *   onUpdated    — callback(updatedNote)  → parent refreshes case after an edit
 *   internal     — when true, uses the internal-notes endpoint + amber styling
 *   currentUser  — { user_id, role } used to gate edit visibility
 */
const NotesThread = ({ caseId, notes = [], legacyNote, onPosted, onUpdated, onDeleted, internal = false, currentUser = null }) => {
  const draftKey = `notes_${internal ? 'internal' : 'public'}_${caseId}`;
  const [draft, setDraft, { clearDraft, hasDraft, savedAt }] = useFormDraft(draftKey, { text: '' });
  const text = draft.text || '';
  const setText = (v) => setDraft({ text: typeof v === 'function' ? v(text) : v });
  const [posting, setPosting] = useState(false);

  const handlePost = async (e) => {
    e?.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setPosting(true);
    try {
      const endpoint = internal ? `/cases/${caseId}/internal-notes` : `/cases/${caseId}/notes`;
      const { data } = await api.post(endpoint, { text: trimmed });
      clearDraft();
      setDraft({ text: '' });
      toast.success(internal ? 'Internal note posted' : 'Note posted');
      onPosted?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPosting(false);
    }
  };

  return (
    <Card data-testid={internal ? 'internal-notes-thread-card' : 'notes-thread-card'}>
      <CardHeader className="border-b border-gray-200 pb-4">
        <CardTitle className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
          <ChatCircleText className={`w-5 h-5 ${internal ? 'text-amber-600' : 'text-[#4CAF50]'}`} />
          {internal ? 'Internal Conversation' : 'Conversation'}
          {internal && <span className="text-xs font-normal text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5">Staff only</span>}
          <span className="text-xs font-normal text-gray-500">({notes.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        {legacyNote && (
          <div className="p-3 bg-gray-50 border border-gray-200" data-testid="legacy-note">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="text-xs rounded-none">Original note</Badge>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{legacyNote}</p>
          </div>
        )}

        {notes.length === 0 && !legacyNote && (
          <p className="text-sm text-gray-500 text-center py-4">No notes yet. Start the conversation below.</p>
        )}

        <div className="space-y-3" data-testid="notes-list">
          {notes.map((n) => (
            <NoteBubble
              key={n.note_id}
              note={n}
              internal={internal}
              caseId={caseId}
              currentUser={currentUser}
              onUpdated={onUpdated}
              onDeleted={onDeleted || onUpdated}
            />
          ))}
        </div>

        <form onSubmit={handlePost} className="pt-2 border-t border-gray-100 space-y-2" data-testid="notes-thread-form">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write a note... (Ctrl+Enter to send)"
            rows={3}
            maxLength={5000}
            className="rounded-none"
            data-testid="notes-thread-input"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handlePost();
              }
            }}
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">
              {text.length}/5000
              {hasDraft && text.trim() && savedAt && (
                <span className="ml-2 inline-flex items-center gap-1 text-blue-600" data-testid={internal ? 'internal-note-draft-saved' : 'note-draft-saved'}>
                  <FloppyDisk className="w-3 h-3" weight="fill" />
                  Draft saved {new Date(savedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </span>
            <Button
              type="submit"
              disabled={posting || !text.trim()}
              className={`${internal ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#4CAF50] hover:bg-[#45a049]'} text-white rounded-none`}
              data-testid="notes-thread-submit"
            >
              <PaperPlaneTilt className="w-4 h-4 mr-2" />
              {posting ? 'Posting...' : 'Post Note'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default NotesThread;
